import { flechitafy, useCrosswordCells } from "./useCrosswordCells"
import "./Crossword.scss"

import { Progress } from "../Progress"
import { say } from "../../../helpers/speech"
import { useTranslation } from "react-i18next"

const EditableHint = ({ hint, onDelete, onChange, onClick }) => {
  //

  const handleChange = (hint, e) => {
    const { done, ...newEntry } = { ...hint, c: e.target.innerHTML }
    onChange(newEntry)
  }

  return (
    <div className="editable-hint" onClick={onClick.bind(this, hint.x, hint.y)}>
      <button
        type="button"
        onClick={() => onDelete(hint.x, hint.y, hint.d)}
        className="btn btn-sm p-0 me-2 tooltip elim-tooltip">
        ❌
      </button>
      <span className="boldy">{hint.n}</span>
      <div
        className="text"
        onInput={handleChange.bind(this, hint)}
        spellCheck="false"
        contentEditable>
        {hint.c}
      </div>
    </div>
  )
}

const ReadOnlyHint = ({ hint, onClick }) => (
  <p
    className={"hint" + (hint.done ? " done" : "")}
    onClick={() => {
      say(hint.c)
      onClick(hint.x, hint.y)
    }}>
    <span className="boldy">{hint.n}</span>
    <span className={"texty " + (hint.focused ? "focused" : "")}>
      {hint.done ? <del>{hint.c}</del> : hint.c}
    </span>
  </p>
)

const Hints = ({
  entries,
  dir,
  activeWrd,
  progress,
  onDelete,
  onChange,
  onClick,
  debugMode,
}) => {
  const { t } = useTranslation()

  if (!entries) return <span>Nada...</span>

  let d = dir === "Across" ? "A" : "D",
    c = flechitafy(d),
    hints = entries
      .filter((entry) => entry.d === d)
      .map((h) =>
        Object.assign(h, {
          done: progress[[h.x, h.y, h.d].join()] === true,
          focused:
            h.n === activeWrd.n && activeWrd.dir.includes(flechitafy(h.d)),
        })
      )

  return (
    <div className="hint-col">
      <center>
        <h5 className="shady">
          {t(`games.crossword.${dir.toLowerCase()}`)} {c}
        </h5>
      </center>
      <div className={"row " + (debugMode ? "m-0" : "px-3")}>
        {hints.map((hint, ix) =>
          debugMode ? (
            <EditableHint
              key={ix}
              onDelete={onDelete}
              onChange={onChange}
              hint={hint}
              onClick={onClick}
            />
          ) : (
            <ReadOnlyHint key={ix} hint={hint} onClick={onClick} />
          )
        )}
      </div>
    </div>
  )
}

const CellEl = ({ cell, active, latent, onClick, debugMode }) => {
  if (cell.disabled) return <div className="cell disabled"></div>

  let cssClass = "cell"

  if (active) cssClass += " active"

  if (latent) cssClass += " latent"

  if (cell.value)
    cssClass +=
      cell.value.toLowerCase() === cell.char.toLowerCase()
        ? " correct"
        : " incorrect"

  return (
    <div className={cssClass} onClick={onClick.bind(this, cell)}>
      {cell.n && <span>{cell.n}</span>}
      {debugMode ? cell.char : cell.value}
    </div>
  )
}

const Grid = ({ cells, active, onClick, debugMode }) => {
  const isActive = (rix, cix) => active.y === rix && active.x === cix,
    isLatent = (rix, cix) =>
      (active.dir === "→" &&
        active.y === rix &&
        // todas las que estan en el medio NO estan deshabilitadas
        cells[rix]
          .slice(Math.min(cix, active.x), Math.max(cix, active.x))
          .every((c) => !c.disabled)) ||
      (active.dir === "↓" &&
        active.x === cix &&
        // todas las que estan en el medio NO estan deshabilitadas
        cells
          .slice(Math.min(rix, active.y), Math.max(rix, active.y))
          .map((row) => row[active.x])
          .every((c) => !c.disabled))

  return (
    <div id="category-container" className="container-sm container-mini">
      <div className="crosswords">
        {cells.map((row, rix) => (
          <div key={rix} className="cell-row">
            {row.map((cell, cix) => (
              <CellEl
                active={isActive(rix, cix)}
                latent={isLatent(rix, cix)}
                key={cix}
                debugMode={debugMode}
                cell={cell}
                onClick={onClick}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export const Crosswords = ({
  size,
  data,
  setData,
  winCallback,
  debugMode = false,
}) => {
  const { t } = useTranslation()
  const cells = useCrosswordCells(size, data, winCallback, debugMode)

  const handleHintClick = (x, y) => cells.handleClick(cells.matrix[y][x])

  const deleteEntry = (x, y, d) => {
    setData((current) =>
      current
        // elimino el seleccionado (x,y,d)
        .filter((entry) => !(entry.x === x && entry.y === y && entry.d === d))
        // actualizo los n
        .reduce((res, c) => {
          const compartenPosicion = res.filter(
            (e) => e.x === c.x && e.y === c.y
          )

          const n =
            compartenPosicion.length > 0
              ? compartenPosicion[0].n
              : res.length
              ? res[res.length - 1].n + 1
              : 1

          return res.concat({ ...c, n })
        }, [])
    )
  }

  const changeHint = (newEntry) => {
    const same = (a, b) => a.x === b.x && a.y === b.y && a.d === b.d
    setData((current) => {
      const ix = data.findIndex((entry) => same(entry, newEntry))
      current[ix] = newEntry
      return current
    })
  }

  return (
    <div className="row mx-0">
      <div className={"col-lg-4 col-12 " + (debugMode ? "" : "py-4")}>
        <Hints
          dir="Across"
          activeWrd={cells.activeWrd}
          debugMode={debugMode}
          entries={data}
          progress={cells.progress}
          onDelete={deleteEntry}
          onChange={changeHint}
          onClick={handleHintClick}
        />
      </div>

      <div
        className={"col-lg-4 col-12 middle-col " + (debugMode ? "pt-4" : "")}>
        <div className="d-flex flex-row justify-content-center my-3">
          <div className="sound-alert">{t("ui.games.msgs.volume_up")}</div>
        </div>
        {!debugMode && <Progress ratio={cells.progress.total} />}
        <Grid
          cells={cells.matrix}
          debugMode={debugMode}
          active={cells.active}
          onClick={cells.handleClick}
        />
      </div>

      <div className={"col-lg-4 col-12 " + (debugMode ? "" : "py-4")}>
        <Hints
          dir="Down"
          activeWrd={cells.activeWrd}
          debugMode={debugMode}
          entries={data}
          progress={cells.progress}
          onDelete={deleteEntry}
          onChange={changeHint}
          onClick={handleHintClick}
        />
      </div>
    </div>
  )
}

export const CrosswordsGame = ({ size, data, winCallback }) =>
  Crosswords({ size, data, winCallback, debugMode: false })
